import {
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  IconButton,
  Stack,
  Alert,
} from "@mui/material";
import StandardButton from "../../../../components/StandardButton";
import React, { useState } from "react";
import useEvents from "../../../../hooks/useEvents";
import BrandingPreview from "./BrandingPreview";
import ColorPickerDialog from "./ColorPickerDialog"; // Import the color picker component
import AdvancedBrandingEditorDialog from "./AdvancedBrandingEditorDialog"; // Import the advanced branding editor component
import useCoachConsoleDocumentIdsVisible from "../../../../hooks/useCoachConsoleDocumentIdsVisible";
import { useAdminDraftOptional } from "../../../../context/AdminDraftContext";

function orgPayloadMatchesLive(payload, mergedOrg) {
  if (!mergedOrg) return false;
  const b = payload.brandingColors || {};
  const mb = mergedOrg.brandingColors || {};
  return (
    (payload.name || "") === (mergedOrg.name || "") &&
    (payload.description || "") === (mergedOrg.description || "") &&
    (payload.welcomeMessage || "") === (mergedOrg.welcomeMessage || "") &&
    (payload.unitSystem || "imperial") ===
      (mergedOrg.unitSystem || "imperial") &&
    (b.primary || "") === (mb.primary || "") &&
    (b.primaryLight || "") === (mb.primaryLight || "") &&
    (b.secondary || "") === (mb.secondary || "") &&
    (b.secondaryLight || "") === (mb.secondaryLight || "") &&
    (b.primaryText || "") === (mb.primaryText || "") &&
    (b.primaryLightText || "") === (mb.primaryLightText || "") &&
    (b.secondaryText || "") === (mb.secondaryText || "") &&
    (b.secondaryLightText || "") === (mb.secondaryLightText || "")
  );
}

// Helper to validate hex colors
const isValidHex = (color) => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
};

// Helper to determine appropriate text color based on background color
const getTextColor = (backgroundColor) => {
  if (!isValidHex(backgroundColor)) return "#ffffff";

  // Convert hex to RGB
  const r = parseInt(backgroundColor.slice(1, 3), 16);
  const g = parseInt(backgroundColor.slice(3, 5), 16);
  const b = parseInt(backgroundColor.slice(5, 7), 16);

  // Calculate brightness using a common formula (same as in AdvancedBrandingEditorDialog)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // Return white for dark backgrounds, black for light backgrounds
  return brightness > 140 ? "#000000" : "#ffffff";
};

// Color picker icon component
const ColorLensIcon = () => (
  <span style={{ fontSize: "18px", cursor: "pointer" }}>🎨</span>
);

const EditOrganization = () => {
  const admin = useAdminDraftOptional();
  const showDocumentIds = useCoachConsoleDocumentIdsVisible();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [unitSystem, setUnitSystem] = useState("imperial");

  const [brandingColors, setBrandingColors] = useState({
    primary: "",
    primaryLight: "",
    secondary: "",
    secondaryLight: "",
    primaryText: "#ffffff",
    primaryLightText: "#ffffff",
    secondaryText: "#ffffff",
    secondaryLightText: "#ffffff",
  });

  // State for color picker
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [currentColorField, setCurrentColorField] = useState(null);
  const [colorPickerInitialValue, setColorPickerInitialValue] =
    useState("#ffffff");

  // State for advanced branding editor
  const [advancedEditorOpen, setAdvancedEditorOpen] = useState(false);

  const events = useEvents();

  const draftPushTimerRef = React.useRef(null);

  const buildOrgPayload = React.useCallback(() => {
    return {
      name,
      description,
      welcomeMessage,
      unitSystem,
      brandingColors: {
        primary: brandingColors.primary,
        primaryLight: brandingColors.primaryLight,
        secondary: brandingColors.secondary,
        secondaryLight: brandingColors.secondaryLight,
        primaryText: brandingColors.primaryText,
        primaryLightText: brandingColors.primaryLightText,
        secondaryText: brandingColors.secondaryText,
        secondaryLightText: brandingColors.secondaryLightText,
      },
    };
  }, [name, description, welcomeMessage, unitSystem, brandingColors]);

  React.useEffect(() => {
    if (!admin) return;
    if (admin.dirty.org) return;
    const organization = admin.mergeOrgForGeneral;
    if (!organization) return;
    setName(organization.name || "");
    setDescription(organization.description || "");
    setUnitSystem(organization.unitSystem ? organization.unitSystem : "imperial");
    setWelcomeMessage(organization.welcomeMessage ? organization.welcomeMessage : "");
    setBrandingColors(
      organization.brandingColors || {
        primary: "",
        primaryLight: "",
        secondary: "",
        secondaryLight: "",
        primaryText: "#ffffff",
        primaryLightText: "#ffffff",
        secondaryText: "#ffffff",
        secondaryLightText: "#ffffff",
      },
    );
  }, [admin, admin?.mergeOrgForGeneral, admin?.dirty.org]);

  React.useEffect(() => {
    if (!admin) return;
    const payload = buildOrgPayload();
    const live = admin.mergeOrgForGeneral;
    if (!live || orgPayloadMatchesLive(payload, live)) {
      return;
    }
    if (draftPushTimerRef.current) clearTimeout(draftPushTimerRef.current);
    draftPushTimerRef.current = setTimeout(() => {
      admin.updateOrganization(buildOrgPayload());
    }, 450);
    return () => {
      if (draftPushTimerRef.current) clearTimeout(draftPushTimerRef.current);
    };
  }, [name, description, welcomeMessage, unitSystem, brandingColors, admin, buildOrgPayload]);

  // Handle color change with validation and update text colors
  const handleColorChange = (colorKey, value) => {
    // Allow empty values or properly formed hex colors
    const updatedColors = { ...brandingColors };

    if (value === "" || isValidHex(value)) {
      updatedColors[colorKey] = value;

      // Update corresponding text color if this is a background color
      if (isValidHex(value)) {
        const textColorKey = `${colorKey}Text`;
        // Only update if this is one of our main color keys (not a text color key)
        if (
          colorKey === "primary" ||
          colorKey === "primaryLight" ||
          colorKey === "secondary" ||
          colorKey === "secondaryLight"
        ) {
          updatedColors[textColorKey] = getTextColor(value);
        }
      }

      setBrandingColors(updatedColors);
    } else if (value.startsWith("#") && value.length <= 7) {
      // Still allow typing but indicate it's invalid
      updatedColors[colorKey] = value;
      setBrandingColors(updatedColors);
    }

    const editEvent = {
      pageName: "Admin",
      textboxName: `Brand Color: ${colorKey}`,
      text: value,
    };
    events.logTextInput(editEvent);
  };

  // Handle opening the color picker
  const handleOpenColorPicker = (colorKey) => {
    setCurrentColorField(colorKey);
    setColorPickerInitialValue(brandingColors[colorKey] || "#ffffff");
    setColorPickerOpen(true);

    // Log the event
    const pickColorEvent = {
      pageName: "Admin",
      buttonName: `Open Color Picker: ${colorKey}`,
    };
    events.logButtonClick(pickColorEvent);
  };

  // Handle color selection from the picker
  const handleColorSelect = (selectedColor) => {
    if (currentColorField) {
      handleColorChange(currentColorField, selectedColor);
    }
  };

  // Handle opening the advanced branding editor
  const handleOpenAdvancedEditor = () => {
    setAdvancedEditorOpen(true);

    // Log the event
    const advancedEditorEvent = {
      pageName: "Admin",
      buttonName: "Open Advanced Branding Editor",
    };
    events.logButtonClick(advancedEditorEvent);
  };

  // Handle saving colors from the advanced editor
  const handleAdvancedEditorSave = (newColors) => {
    // Calculate and add text colors for each background color
    const enhancedColors = {
      ...newColors,
      primaryText: getTextColor(newColors.primary),
      primaryLightText: getTextColor(newColors.primaryLight),
      secondaryText: getTextColor(newColors.secondary),
      secondaryLightText: getTextColor(newColors.secondaryLight),
    };

    setBrandingColors(enhancedColors);

    // Log the event
    const saveAdvancedColorsEvent = {
      pageName: "Admin",
      buttonName: "Save Advanced Branding Colors",
    };
    events.logButtonClick(saveAdvancedColorsEvent);
  };

  // Generate color suggestions based on primary color with contrast checking for white text
  const suggestColors = () => {
    const primary = brandingColors.primary;

    if (!primary || !isValidHex(primary)) {
      return;
    }

    // Convert hex to RGB
    const hexToRgb = (hex) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return { r, g, b };
    };

    // Convert RGB to hex
    const rgbToHex = ({ r, g, b }) => {
      return `#${Math.round(r).toString(16).padStart(2, "0")}${Math.round(g).toString(16).padStart(2, "0")}${Math.round(b).toString(16).padStart(2, "0")}`;
    };

    // Calculate relative luminance for WCAG contrast
    const getLuminance = ({ r, g, b }) => {
      const rsRGB = r / 255;
      const gsRGB = g / 255;
      const bsRGB = b / 255;

      const R =
        rsRGB <= 0.03928
          ? rsRGB / 12.92
          : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
      const G =
        gsRGB <= 0.03928
          ? gsRGB / 12.92
          : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
      const B =
        bsRGB <= 0.03928
          ? bsRGB / 12.92
          : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

      return 0.2126 * R + 0.7152 * G + 0.0722 * B;
    };

    // Calculate contrast ratio between colors
    const getContrastRatio = (rgb1, rgb2) => {
      const lum1 = getLuminance(rgb1);
      const lum2 = getLuminance(rgb2);
      const brightest = Math.max(lum1, lum2);
      const darkest = Math.min(lum1, lum2);
      return (brightest + 0.05) / (darkest + 0.05);
    };

    // Check if color is too light for white text (we want to ensure good contrast with white text)
    const isTooLightForWhiteText = (rgb) => {
      const WHITE = { r: 255, g: 255, b: 255 };
      const MIN_CONTRAST = 4.5; // WCAG AA standard for normal text

      return getContrastRatio(rgb, WHITE) < MIN_CONTRAST;
    };

    // Darken a color to ensure good contrast with white text
    const ensureContrastWithWhiteText = (rgb) => {
      const WHITE = { r: 255, g: 255, b: 255 };
      const MIN_CONTRAST = 4.5; // WCAG AA standard for normal text

      const adjustedRgb = { ...rgb };

      // If contrast with white is too low, darken the color
      while (getContrastRatio(adjustedRgb, WHITE) < MIN_CONTRAST) {
        // Darken by reducing RGB values
        adjustedRgb.r = Math.max(0, adjustedRgb.r * 0.9);
        adjustedRgb.g = Math.max(0, adjustedRgb.g * 0.9);
        adjustedRgb.b = Math.max(0, adjustedRgb.b * 0.9);

        // Safety exit if we're getting too dark
        if (adjustedRgb.r < 20 && adjustedRgb.g < 20 && adjustedRgb.b < 20) {
          // Return a medium-dark value that should have good contrast with white
          return { r: 50, g: 50, b: 50 };
        }
      }

      return adjustedRgb;
    };

    // Create a shade of a color (darker or lighter)
    const createShade = (rgb, factor, ensureWhiteTextContrast = false) => {
      // Factor > 1 lightens, factor < 1 darkens
      const result = {
        r: Math.min(255, Math.max(0, rgb.r * factor)),
        g: Math.min(255, Math.max(0, rgb.g * factor)),
        b: Math.min(255, Math.max(0, rgb.b * factor)),
      };

      // If we need to ensure contrast with white text and the color is too light
      if (ensureWhiteTextContrast && isTooLightForWhiteText(result)) {
        return ensureContrastWithWhiteText(result);
      }

      return result;
    };

    // Adjust color saturation
    const adjustSaturation = (rgb, factor) => {
      // Convert RGB to HSL
      const r = rgb.r / 255;
      const g = rgb.g / 255;
      const b = rgb.b / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h,
        s,
        // eslint-disable-next-line prefer-const
        l = (max + min) / 2;

      if (max === min) {
        h = s = 0; // achromatic
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
        }

        h /= 6;
      }

      // Adjust saturation
      s = Math.min(1, Math.max(0, s * factor));

      // Convert back to RGB
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      const hueToRgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      return {
        r: Math.round(hueToRgb(p, q, h + 1 / 3) * 255),
        g: Math.round(hueToRgb(p, q, h) * 255),
        b: Math.round(hueToRgb(p, q, h - 1 / 3) * 255),
      };
    };

    // Start with the primary color
    const primaryRgb = hexToRgb(primary);

    // Create a slightly more saturated primary color for better visual appeal
    const adjustedPrimaryRgb = adjustSaturation(primaryRgb, 1.2);
    const adjustedPrimary = rgbToHex(adjustedPrimaryRgb);

    // Create lighter version for primaryLight, ensure it works with white text
    const primaryLightRgb = createShade(adjustedPrimaryRgb, 1.3, true);
    const primaryLight = rgbToHex(primaryLightRgb);

    // Create complementary color for secondary, ensure it works with white text
    const hueShiftedSecondaryRgb = adjustSaturation(
      {
        r: 255 - adjustedPrimaryRgb.r,
        g: 255 - adjustedPrimaryRgb.g,
        b: 255 - adjustedPrimaryRgb.b,
      },
      1.2,
    );

    // Ensure secondary has good contrast with white text
    const secondaryRgb = isTooLightForWhiteText(hueShiftedSecondaryRgb)
      ? ensureContrastWithWhiteText(hueShiftedSecondaryRgb)
      : hueShiftedSecondaryRgb;

    const secondary = rgbToHex(secondaryRgb);

    // Create lighter version of secondary, ENSURING it works with white text
    // This is critical for preventing white-on-white text issues
    const secondaryLightRgb = createShade(secondaryRgb, 1.3, true);
    const secondaryLight = rgbToHex(secondaryLightRgb);

    // Update the color values and calculate corresponding text colors
    setBrandingColors({
      primary: adjustedPrimary,
      primaryLight: primaryLight,
      secondary: secondary,
      secondaryLight: secondaryLight,
      primaryText: getTextColor(adjustedPrimary),
      primaryLightText: getTextColor(primaryLight),
      secondaryText: getTextColor(secondary),
      secondaryLightText: getTextColor(secondaryLight),
    });

    // Log the event
    const suggestEvent = {
      pageName: "Admin",
      buttonName: "Auto-suggest Colors",
    };
    events.logButtonClick(suggestEvent);
  };

  return (
    <>
      <Box>
        <Typography
          style={{ fontSize: 20, fontWeight: "500", marginBottom: 12 }}
        >
          Organization Information
        </Typography>

        <Alert severity="info" sx={{ mb: 2, maxWidth: 720 }}>
          Organization settings are part of the <strong>admin draft</strong>.
          Use <strong>Publish all changes</strong> at the top of the Admin page
          when you are ready.
        </Alert>

        {showDocumentIds ? (
          <Typography style={{ fontSize: 10, color: "#666", marginBottom: 8 }}>
            Org ID: {admin?.liveOrg?.id || "Loading..."}
          </Typography>
        ) : null}

        <Typography>Organization Name</Typography>
        <TextField
          style={{ width: 350 }}
          fullWidth={false}
          variant="outlined"
          value={name || ""}
          placeholder="Enter organization name"
          onChange={(event) => {
            let value = event.target.value;
            if (value.length > 40) {
              value = value.substring(0, 40);
            }
            setName(value);
            const editOrgNameEvent = {
              pageName: "Admin",
              textboxName: "Organization Name",
              text: value,
            };
            events.logTextInput(editOrgNameEvent);
          }}
        />
        <Typography style={{ marginTop: 12 }}>Organization Slogan</Typography>
        <TextField
          style={{ width: 400 }}
          fullWidth={false}
          variant="outlined"
          value={description || ""}
          placeholder="Enter organization slogan"
          onChange={(event) => {
            let value = event.target.value;
            if (value.length > 50) {
              value = value.substring(0, 50);
            }
            const editOrgNameEvent = {
              pageName: "Admin",
              textboxName: "Organization Slogan",
              text: value,
            };
            events.logTextInput(editOrgNameEvent);
            setDescription(value);
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" style={{ marginTop: 24, marginBottom: 8 }}>
            Branding Colors
          </Typography>

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <StandardButton
              variant="secondary"
              size="small"
              onClick={suggestColors}
              disabled={
                !brandingColors.primary || !isValidHex(brandingColors.primary)
              }
            >
              Auto-suggest Colors
            </StandardButton>
            <StandardButton
              variant="primary"
              size="small"
              onClick={handleOpenAdvancedEditor}
            >
              Advanced
            </StandardButton>
          </Box>
        </Box>

        <Typography style={{ fontSize: 14, marginBottom: 16, color: "#666" }}>
          Set the branding colors for your organization using hex color codes
          (e.g., #FF5733) or use the color picker.
        </Typography>

        <Stack direction={"row"} spacing={2} style={{ marginBottom: 16 }}>
          <TextField
            label="Primary Color"
            value={brandingColors?.primary || ""}
            onChange={(e) => handleColorChange("primary", e.target.value)}
            error={
              brandingColors?.primary && !isValidHex(brandingColors.primary)
            }
            helperText={
              brandingColors?.primary && !isValidHex(brandingColors.primary)
                ? "Invalid hex color"
                : ""
            }
            InputProps={{
              startAdornment: brandingColors?.primary ? (
                <InputAdornment position="start">
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: isValidHex(brandingColors.primary)
                        ? brandingColors.primary
                        : "#ccc",
                      borderRadius: "3px",
                      marginRight: 8,
                      border: "1px solid rgba(0,0,0,0.1)",
                    }}
                  />
                </InputAdornment>
              ) : null,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Use color picker">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenColorPicker("primary")}
                    >
                      <ColorLensIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            style={{ width: 300 }}
          />

          <TextField
            label="Primary Light Color"
            value={brandingColors?.primaryLight || ""}
            onChange={(e) => handleColorChange("primaryLight", e.target.value)}
            error={
              brandingColors?.primaryLight &&
              !isValidHex(brandingColors.primaryLight)
            }
            helperText={
              brandingColors?.primaryLight &&
              !isValidHex(brandingColors.primaryLight)
                ? "Invalid hex color"
                : ""
            }
            InputProps={{
              startAdornment: brandingColors?.primaryLight ? (
                <InputAdornment position="start">
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: isValidHex(brandingColors.primaryLight)
                        ? brandingColors.primaryLight
                        : "#ccc",
                      borderRadius: "3px",
                      marginRight: 8,
                      border: "1px solid rgba(0,0,0,0.1)",
                    }}
                  />
                </InputAdornment>
              ) : null,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Use color picker">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenColorPicker("primaryLight")}
                    >
                      <ColorLensIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            style={{ width: 300 }}
          />

          <TextField
            label="Secondary Color"
            value={brandingColors?.secondary || ""}
            onChange={(e) => handleColorChange("secondary", e.target.value)}
            error={
              brandingColors?.secondary && !isValidHex(brandingColors.secondary)
            }
            helperText={
              brandingColors?.secondary && !isValidHex(brandingColors.secondary)
                ? "Invalid hex color"
                : ""
            }
            InputProps={{
              startAdornment: brandingColors?.secondary ? (
                <InputAdornment position="start">
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: isValidHex(brandingColors.secondary)
                        ? brandingColors.secondary
                        : "#ccc",
                      borderRadius: "3px",
                      marginRight: 8,
                      border: "1px solid rgba(0,0,0,0.1)",
                    }}
                  />
                </InputAdornment>
              ) : null,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Use color picker">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenColorPicker("secondary")}
                    >
                      <ColorLensIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            style={{ width: 300 }}
          />

          <TextField
            label="Secondary Light Color"
            value={brandingColors?.secondaryLight || ""}
            onChange={(e) =>
              handleColorChange("secondaryLight", e.target.value)
            }
            error={
              brandingColors?.secondaryLight &&
              !isValidHex(brandingColors.secondaryLight)
            }
            helperText={
              brandingColors?.secondaryLight &&
              !isValidHex(brandingColors.secondaryLight)
                ? "Invalid hex color"
                : ""
            }
            InputProps={{
              startAdornment: brandingColors?.secondaryLight ? (
                <InputAdornment position="start">
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: isValidHex(brandingColors.secondaryLight)
                        ? brandingColors.secondaryLight
                        : "#ccc",
                      borderRadius: "3px",
                      marginRight: 8,
                      border: "1px solid rgba(0,0,0,0.1)",
                    }}
                  />
                </InputAdornment>
              ) : null,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Use color picker">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenColorPicker("secondaryLight")}
                    >
                      <ColorLensIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            style={{ width: 300 }}
          />
        </Stack>

        <BrandingPreview colors={brandingColors} />

        <Typography style={{ marginTop: 24 }}>
          Organization Welcome Chat Message
        </Typography>
        <Typography
          style={{ marginTop: 0, fontSize: 14, marginBottom: 4, color: "#666" }}
        >
          This message appears in a new users chat group when they are added to
          your organization.
        </Typography>
        <TextField
          multiline
          maxRows={6}
          minRows={6}
          fullWidth={true}
          variant="outlined"
          value={welcomeMessage}
          onChange={(event) => {
            setWelcomeMessage(event.target.value);
            const editOrgNameEvent = {
              pageName: "Admin",
              textboxName: "Organization Welcome Message",
              text: event.target.value,
            };
            events.logTextInput(editOrgNameEvent);
          }}
        />

        <Typography style={{ marginTop: 12 }}>Measurement Units</Typography>
        <Select
          style={{ width: 300 }}
          variant="outlined"
          value={unitSystem}
          onChange={(event) => {
            setUnitSystem(event.target.value);
            const editOrgNameEvent = {
              pageName: "Admin",
              textboxName: "Measurement Units",
              text: event.target.value,
            };
            events.logTextInput(editOrgNameEvent);
          }}
        >
          <MenuItem value="imperial">Imperial</MenuItem>
          <MenuItem value="metric">Metric</MenuItem>
        </Select>

        {admin?.dirty.org ? (
          <Typography variant="body2" sx={{ color: "#5f6368", mt: 2 }}>
            General settings are in your draft — use <strong>Publish all changes</strong>{" "}
            at the top when ready.
          </Typography>
        ) : null}
      </Box>

      {/* Color Picker Dialog */}
      <ColorPickerDialog
        open={colorPickerOpen}
        onClose={() => setColorPickerOpen(false)}
        initialColor={colorPickerInitialValue}
        onColorSelect={handleColorSelect}
        title={`Select ${currentColorField ? currentColorField.replace(/([A-Z])/g, " $1").toLowerCase() : ""} color`}
      />

      {/* Advanced Branding Editor Dialog */}
      <AdvancedBrandingEditorDialog
        open={advancedEditorOpen}
        onClose={() => setAdvancedEditorOpen(false)}
        initialColors={brandingColors}
        onSave={handleAdvancedEditorSave}
      />
    </>
  );
};

export default EditOrganization;
